import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent } from "react"
import { StablePortal } from "@/components/layout/shell"
import {
  AlertTriangle,
  FolderTree,
  Inbox,
  ChevronRight,
  Zap,
  Bot,
} from "lucide-react"
import { GoogleDriveIcon } from "@/components/icons/google-drive-icon"
import { BasecampIcon } from "@/components/icons/basecamp-icon"
import { NextcloudIcon } from "@/components/icons/nextcloud-icon"
import { BasecampSidebarPanel } from "@/components/integrations/basecamp/BasecampSidebarPanel"
import { NextcloudSidebarPanel } from "@/components/integrations/nextcloud/NextcloudSidebarPanel"
import { NextcloudOptionsMenu } from "@/components/integrations/nextcloud/NextcloudOptionsMenu"
import { NEXTCLOUD_OPEN_EVENT } from "@/components/integrations/nextcloud/nextcloud-browse-events"
import { useExtensionEnabledInAnyWorkspace } from "@/hooks/use-extension-enabled-in-any-workspace"
import { type OpenTab } from "@/components/features/document-tabs"
import type { TabMenuHandlers } from "@/components/features/tab-context-menu-host"
import { usePersistedState } from "@/hooks/use-persisted-state"
import { StorageKeys, StorageSchemas } from "@/lib/storage"
import { useIntegrations } from "@/hooks/use-integrations"
import { GraphView } from "./views/graph-view"
import { TagsView } from "./views/tags-view"
import { TrashView } from "./views/trash-view"
import { FlowsManager } from "@/components/features/flows-manager"
import { ChatsSidebarPanel, ChatsSidebarPanelMenu } from "@/components/features/chats/ChatsSidebarPanel"
import { DriveSidebar } from "@/components/integrations/google-workspace/DriveSidebar"
import { DriveImportPickerHost } from "@/components/integrations/google-workspace/drive-import-picker"
import { DriveOptionsMenu } from "@/components/integrations/google-workspace/drive-options-menu"
import { openGoogleDriveImportPicker } from "@/components/integrations/google-workspace/gdoc-import-utils"
import { useGoogleDriveAuth } from "@/hooks/use-google-drive-auth"
import { useBasecampAuth, useBasecampAuthWatch } from "@/hooks/use-basecamp"
import {
  dismissBasecampDisconnectedToast,
  showBasecampDisconnectedToast,
} from "@/lib/basecamp-api"
import { toast } from "sonner"
import { sidebarModeEventName, type SidebarPrimaryMode } from "@/lib/sidebar-mode"
import { suggestedExtensionRailId } from "@/lib/sidebar-rail-suggested"
import { InboxSidebarView } from "./views/inbox-sidebar-view"
import { ContextMenu } from "@/components/features/context-menu"
import { RenameDialog } from "@/components/features/rename-dialog"
import { FolderPickerDialog, type WorkspaceOption } from "@/components/features/folder-picker-dialog"
import { useWorkspace } from "@/providers/workspace-provider"
import type { TreeData } from "@/components/ui/tree-view"
import type { DocumentListItemData } from "@/components/ui/document-list-item"
import {
  fetchWithAuth,
  getSidebarShortcuts,
  createSidebarShortcut,
  deleteSidebarShortcut,
  reorderSidebarShortcuts,
  resolveDocumentRowId,
  type SidebarShortcut,
} from "@/lib/api"
import { isAbortFetchError } from "@/lib/api/core"
import { findPinnedSidebarShortcut } from "@/lib/api/shortcuts"
import { buildOpenTabIndex, openTabPathsFromIndex } from "@/lib/open-tab-match"
import { formatUserFacingStoragePath } from "@/lib/app-routes"
import { testIds } from "@/lib/test-ids"
import { cn } from "@/lib/utils"
import { useInbox } from "@/hooks/use-inbox"
import { isThreadWorkspaceVisible } from "@/components/features/inbox/mappers"
import { filterInboxTriageItems } from "@/lib/inbox-thread-filters"
import {
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core'
import {
  arrayMove,
} from '@dnd-kit/sortable'
import { BrowsePanel } from "./left-sidebar.browse-panel"
import { BrowseFolderFocusProvider } from "@/providers/browse-folder-focus-provider"
import {
  SidebarOpenTabsSection,
  groupOpenTabsByWorkspace,
} from "./left-sidebar.open-tabs"
import { SidebarPanelMenu } from "./left-sidebar.panel-menu"
import { SidebarPanelHeader } from "@/components/layout/sidebar/SidebarPanelHeader"
import { useSidebarDensity } from "@/hooks/use-sidebar-density"
import type { ExplorerFolderTreeNode } from "@/components/layout/documents-explorer-inline-section"
import {
  pinnedShortcutsForWorkspace,
  resolvePinnedWorkspaceIdForShortcut,
} from "./left-sidebar.utils"
import { resolveWorkspaceForDocument } from "@/components/layout/resizable-layout.handlers/shared"
import { markDocumentForEditOnOpen } from "@/lib/pending-document-edit-focus"
import { openSettings, openWorkspaceExtensionsSettings } from "@/lib/open-settings"
import { useShellChrome } from "./resizable-layout.flyout"
import { LeftSidebarIconRail, type RailItem } from "./left-sidebar.rail"
type SidebarTab = "browse-nav" | "browse" | "documents-tree" | "documents" | "inbox" | "flows" | "chats" | "drive" | "basecamp" | "nextcloud" | "graph" | "tags" | "trash" | `integration-${string}`

type ContextMenuContext = {
  type: "tag" | "document" | "folder" | "project" | "tab" | "extension-entity" | "flow"
  value: string
  path?: string
  filePath?: string
  tags?: string[]
  documentId?: string
  tabId?: string
  tabType?: string
  isPinned?: boolean
  hasFilePath?: boolean
  isListTab?: boolean
  isNewTab?: boolean
  extensionId?: string
  extensionEntityType?: string
  extensionEntity?: unknown
  /** Sidebar Documents row when workspace has no content_root - show app menu, gate create actions */
  missingContentRoot?: boolean
  workspaceId?: string
  flowId?: string
  flowEnabled?: boolean
  browseHiddenChildCount?: number
  browseHiddenLabel?: string
}

type ContextMenuState = {
  x: number
  y: number
  context: ContextMenuContext
} | null

interface LeftSidebarProps {
  openTabs?: OpenTab[]
  activeTabId?: string | null
  activeDocumentPath?: string | null  // NEW: File path of currently open document
  onDocumentClick?: (documentId: string, event?: React.MouseEvent) => void
  onDocumentClose?: (documentId: string) => void
  onCloseAllOpenDocuments?: () => void
  onCloseManyDocuments?: (documentIds: string[]) => void
  onDocumentReorder?: (reorderedDocuments: OpenTab[]) => void
  onDocumentListToggle?: () => void
  onProjectClick?: (projectId: string, event?: React.MouseEvent) => void
  showDocumentList?: boolean
  collapsed?: boolean
  onToggleCollapse?: () => void
  selectedProjectId?: string | null
  onDragToChat?: (nodeId: string, title: string, path: string, type: 'file' | 'folder') => void  // NEW: Drag to chat handler
  onTabDragToChat?: (tabId: string, title: string, type: OpenTab["type"]) => void  // NEW: Tab drag to chat handler
  onAddToChat?: (item: { type: "file" | "folder"; id: string; path: string; title?: string }) => void  // NEW: Add to chat handler (for context menu)
  onHomeClick?: (event?: React.MouseEvent) => void  // NEW: Home button click handler
  onInboxClick?: () => void
  onDriveClick?: () => void
  /** Bumped from ResizableLayout on content-revision / document mutations so Browse tree refetches reliably. */
  browseExplorerRefreshToken?: number
  onOpenDrivePreview?: (
    file: { id: string; name: string; mimeType: string; webViewLink?: string },
    options: { organizationId: string; event?: React.MouseEvent; importedDocumentId?: string },
  ) => void
  /** Shared tab context-menu handlers for Open Tabs rows (pin/close/…). */
  tabMenuHandlers?: TabMenuHandlers
}

type FolderTreeNode = ExplorerFolderTreeNode

export function LeftSidebar({
  openTabs = [],
  activeTabId = null,
  activeDocumentPath = null,
  onDocumentClick = () => {},
  onDocumentClose = () => {},
  onCloseAllOpenDocuments = () => {},
  onCloseManyDocuments = () => {},
  onDocumentReorder = () => {},
  onDocumentListToggle = () => {},
  onProjectClick = () => {},
  showDocumentList = true,
  collapsed = false,
  onToggleCollapse = () => {},
  selectedProjectId = null,
  onDragToChat,
  onTabDragToChat,
  onAddToChat,
  onHomeClick,
  onInboxClick,
  onDriveClick,
  browseExplorerRefreshToken = 0,
  onOpenDrivePreview,
  tabMenuHandlers,
}: LeftSidebarProps) {
  const { flyoutPortal, autoHide, openFlyout } = useShellChrome()
  const [dockedModeSlot, setDockedModeSlot] = useState<HTMLDivElement | null>(null)
  // console.log('[LeftSidebar] Props received:', { activeTabId, activeDocumentPath })
  const [activeTab, setActiveTab] = usePersistedState<SidebarTab>({
    key: StorageKeys.DOCUMENTS_ACTIVE_SIDEBAR_TAB,
    defaultValue: "browse-nav",
    schema: StorageSchemas.sidebarTab,
  })
  const { workspaces: allWorkspaces, activeWorkspaceIds, toggleWorkspace, hideOtherWorkspaces } = useWorkspace()
  const drivePanelOrgId = activeWorkspaceIds[0] ?? allWorkspaces[0]?.id
  const [sidebarOpenTabsVisible, setSidebarOpenTabsVisible] = usePersistedState({
    key: StorageKeys.SIDEBAR_OPEN_TABS_VISIBLE,
    defaultValue: true,
    schema: StorageSchemas.boolean,
  })
  const { dense: sidebarDense, setDense: setSidebarDense } = useSidebarDensity()
  const { authStatus: driveAuthStatus } = useGoogleDriveAuth(drivePanelOrgId)
  const { authStatus: basecampAuthStatus } = useBasecampAuth(drivePanelOrgId)
  const { disconnected: disconnectedBasecamp } = useBasecampAuthWatch(activeWorkspaceIds)
  const basecampDisconnected = disconnectedBasecamp.length > 0
  const basecampReconnect = disconnectedBasecamp[0]
  const focusedOpenTab = openTabs.find((t) => t.id === activeTabId || t.tabSlotId === activeTabId)
  const onBasecampSurface =
    activeTab === 'basecamp' ||
    (typeof focusedOpenTab?.type === 'string' && focusedOpenTab.type.startsWith('integration-basecamp'))

  useEffect(() => {
    if (!onBasecampSurface || !basecampDisconnected) {
      dismissBasecampDisconnectedToast()
      return
    }
    showBasecampDisconnectedToast(basecampReconnect?.status?.auth_url)
  }, [
    onBasecampSurface,
    basecampDisconnected,
    basecampReconnect?.id,
    basecampReconnect?.status?.auth_url,
  ])
  const { enabled: showBasecampRail, loading: basecampRailLoading } = useExtensionEnabledInAnyWorkspace(
    'basecamp',
    activeWorkspaceIds,
  )
  const { enabled: showNextcloudRail, enabledIds: nextcloudEnabledIds, loading: nextcloudRailLoading } = useExtensionEnabledInAnyWorkspace(
    'nextcloud',
    activeWorkspaceIds,
  )
  const nextcloudCandidates = useMemo(
    () => activeWorkspaceIds.filter((id) => allWorkspaces.some((workspace) => workspace.id === id)),
    [activeWorkspaceIds, allWorkspaces],
  )
  const [pickedNextcloudId, setPickedNextcloudId] = useState<string | null>(null)
  const nextcloudFocusId =
    pickedNextcloudId && nextcloudCandidates.includes(pickedNextcloudId)
      ? pickedNextcloudId
      : nextcloudEnabledIds.find((id) => nextcloudCandidates.includes(id)) ?? nextcloudCandidates[0] ?? ''

  useEffect(() => {
    if (basecampRailLoading || nextcloudRailLoading) return
    if (activeTab === 'basecamp' && !showBasecampRail) {
      setActiveTab('browse-nav')
    }
    if (activeTab === 'nextcloud' && !showNextcloudRail) {
      setActiveTab('browse-nav')
    }
  }, [
    activeTab,
    showBasecampRail,
    showNextcloudRail,
    basecampRailLoading,
    nextcloudRailLoading,
    setActiveTab,
  ])

  const handleDriveConnect = useCallback(async () => {
    if (!drivePanelOrgId) return
    try {
      const res = await fetchWithAuth(
        `/api/google-drive/auth/url?organizationId=${encodeURIComponent(drivePanelOrgId)}`,
      )
      if (!res.ok) throw new Error('Failed to get auth URL')
      const data = await res.json() as { url?: string }
      if (data.url) {
        window.open(data.url, 'google-drive-oauth', 'width=500,height=650,popup')
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to start Google Drive connection')
    }
  }, [drivePanelOrgId])

  const handleDriveImportFromDrive = useCallback(() => {
    openGoogleDriveImportPicker({ organizationId: drivePanelOrgId })
  }, [drivePanelOrgId])

  const sidebarPanelContentRef = useRef<HTMLDivElement>(null)
  const tabsByWorkspace = useMemo(
    () => groupOpenTabsByWorkspace(openTabs, allWorkspaces, activeWorkspaceIds),
    [openTabs, allWorkspaces, activeWorkspaceIds],
  )
  const hasOpenTabs = tabsByWorkspace.some((g) => g.tabs.length > 0)
  const openTabsTotalCount = tabsByWorkspace.reduce((n, { tabs }) => n + tabs.length, 0)
  const { items: inboxItems } = useInbox()
  // Scope the badge to the same active-workspace set the Inbox "All" tab filters by
  // (InboxFeed.tsx), so the badge never shows unread threads the list can't display.
  // isThreadWorkspaceVisible treats the 'personal' sentinel (user-level threads, e.g.
  // workspace invite notifications) as always visible regardless of active workspace.
  // Agent chats use taggedOrganizationIds when present (same as Chats sidebar).
  const inboxUnreadCount = useMemo(() => {
    return filterInboxTriageItems(inboxItems).filter(
      (i) =>
        i.status === 'unread' &&
        isThreadWorkspaceVisible(
          (i.meta?.workspaceId as string) ?? 'personal',
          activeWorkspaceIds,
          i.taggedOrganizationIds,
        ),
    ).length
  }, [inboxItems, activeWorkspaceIds])
  const [activeExtension, setActiveExtension] = useState<string | null>(null)

  // Sidebar shortcuts state (lifted from BrowsePanel so handlePinToSidebar has access)
  const [shortcuts, setShortcuts] = useState<SidebarShortcut[]>([])
  const [shortcutsLoading, setShortcutsLoading] = useState(false)
  const [activeShortcutId, setActiveShortcutId] = useState<string | null>(null)

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    })
  )

  useEffect(() => {
    const handler = (e: Event) => {
      const mode = (e as CustomEvent<{ mode?: SidebarPrimaryMode }>).detail?.mode
      if (
        mode === 'inbox' ||
        mode === 'flows' ||
        mode === 'chats' ||
        mode === 'drive' ||
        mode === 'browse-nav' ||
        mode === 'documents-tree'
      ) {
        setActiveTab(mode)
      }
    }
    window.addEventListener(sidebarModeEventName(), handler)
    return () => window.removeEventListener(sidebarModeEventName(), handler)
  }, [setActiveTab])

  useEffect(() => {
    const handler = () => {
      if (showNextcloudRail) setActiveTab('nextcloud')
    }
    window.addEventListener(NEXTCLOUD_OPEN_EVENT, handler)
    return () => window.removeEventListener(NEXTCLOUD_OPEN_EVENT, handler)
  }, [setActiveTab, showNextcloudRail])

  useEffect(() => {
    let mounted = true
    const fetchShortcuts = async () => {
      setShortcutsLoading(true)
      try {
        const result = await getSidebarShortcuts()
        if (mounted) setShortcuts(result.shortcuts || [])
      } catch (error) {
        if (!isAbortFetchError(error)) {
          console.error('[Sidebar] Failed to fetch shortcuts:', error)
        }
      } finally {
        if (mounted) setShortcutsLoading(false)
      }
    }
    fetchShortcuts()
    return () => { mounted = false }
  }, [])

  useEffect(() => {
    const handleShortcutChange = () => {
      getSidebarShortcuts().then(result => setShortcuts(result.shortcuts || [])).catch(console.error)
    }
    window.addEventListener('sidebar-shortcuts-changed', handleShortcutChange)
    return () => window.removeEventListener('sidebar-shortcuts-changed', handleShortcutChange)
  }, [])

  /** Debounced GET /api/sidebar-shortcuts so pin labels (document_title/path) refresh without reloading the explorer tree. */
  const pinnedShortcutsMetaRefreshTimerRef = useRef<number | null>(null)
  const queuePinnedShortcutsMetadataRefresh = useCallback(() => {
    if (pinnedShortcutsMetaRefreshTimerRef.current) clearTimeout(pinnedShortcutsMetaRefreshTimerRef.current)
    pinnedShortcutsMetaRefreshTimerRef.current = window.setTimeout(() => {
      pinnedShortcutsMetaRefreshTimerRef.current = null
      void getSidebarShortcuts()
        .then((r) => setShortcuts(r.shortcuts || []))
        .catch(console.error)
    }, 2300)
  }, [])

  useEffect(() => () => {
    if (pinnedShortcutsMetaRefreshTimerRef.current) clearTimeout(pinnedShortcutsMetaRefreshTimerRef.current)
  }, [])

  useEffect(() => {
    const bump = () => queuePinnedShortcutsMetadataRefresh()
    window.addEventListener(
      'sidebar-pinned-shortcuts-metadata-refresh',
      bump as EventListener,
    )
    window.addEventListener('flownotes-content-revision', bump as EventListener)
    window.addEventListener('flownotes-structure-revision', bump as EventListener)
    window.addEventListener('document-renamed', bump as EventListener)
    return () => {
      window.removeEventListener(
        'sidebar-pinned-shortcuts-metadata-refresh',
        bump as EventListener,
      )
      window.removeEventListener('flownotes-content-revision', bump as EventListener)
      window.removeEventListener('flownotes-structure-revision', bump as EventListener)
      window.removeEventListener('document-renamed', bump as EventListener)
    }
  }, [queuePinnedShortcutsMetadataRefresh])

  const handleShortcutDragStart = useCallback((event: DragStartEvent) => {
    setActiveShortcutId(event.active.id as string)
  }, [])

  const handleShortcutDragEnd = useCallback(async (event: DragEndEvent) => {
    const { active, over } = event
    setActiveShortcutId(null)
    if (!over || active.id === over.id) return

    const activeSc = shortcuts.find((s) => s.id === active.id)
    const overSc = shortcuts.find((s) => s.id === over.id)
    const activeWsId = resolvePinnedWorkspaceIdForShortcut(activeSc, allWorkspaces)
    const overWsId = resolvePinnedWorkspaceIdForShortcut(overSc, allWorkspaces)
    if (!activeSc || !overSc || !activeWsId || activeWsId !== overWsId) return

    const ws = allWorkspaces.find((w) => w.id === activeWsId)
    if (!ws) return

    const orgPinsOrdered = pinnedShortcutsForWorkspace(shortcuts, ws)
    const oldIndex = orgPinsOrdered.findIndex((s) => s.id === active.id)
    const newIndex = orgPinsOrdered.findIndex((s) => s.id === over.id)
    if (oldIndex === -1 || newIndex === -1) return

    const reorderedOrgPins = arrayMove(orgPinsOrdered, oldIndex, newIndex).map((shortcut, index) => ({
      ...shortcut,
      sort_order: index,
    }))

    const other = shortcuts.filter((s) => !reorderedOrgPins.some((r) => r.id === s.id))
    const nextShortcuts = [...other, ...reorderedOrgPins].sort((a, b) => {
      const orgA = resolvePinnedWorkspaceIdForShortcut(a, allWorkspaces) ?? ''
      const orgB = resolvePinnedWorkspaceIdForShortcut(b, allWorkspaces) ?? ''
      const c = orgA.localeCompare(orgB)
      if (c !== 0) return c
      const so = (a.sort_order ?? 0) - (b.sort_order ?? 0)
      if (so !== 0) return so
      return new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    })

    setShortcuts(nextShortcuts)

    try {
      const updates = reorderedOrgPins.map((shortcut, index) => ({
        id: shortcut.id,
        sort_order: index,
      }))
      await reorderSidebarShortcuts(updates)
    } catch (error) {
      console.error('[Sidebar] Failed to reorder shortcuts:', error)
      getSidebarShortcuts().then((result) => setShortcuts(result.shortcuts || [])).catch(console.error)
      const { toast } = await import('sonner')
      toast.error('Failed to reorder shortcuts')
    }
  }, [shortcuts, allWorkspaces])

  const handleInboxExtensionChange = useCallback((id: string | null) => {
    setActiveExtension(id)
    window.dispatchEvent(new CustomEvent('inbox-filter-change', {
      detail: { kindFilter: 'all', activeExtension: id },
    }))
  }, [])
  const moveDialogWorkspaces: WorkspaceOption[] = (allWorkspaces || []).map((w) => ({
    id: w.id,
    name: w.name,
    slug: w.slug,
    contentRoot: (w.contentRoot ?? '').trim()
  }))
  const { configuredIntegrations, loading } = useIntegrations()
  const [contextMenu, setContextMenu] = useState<ContextMenuState>(null)

  /** Explorer tree passes file paths as documentId; DB pins use UUID + document_path — compare both */
  const sidebarShortcutContextIsPinned = useMemo(() => {
    const ctx = contextMenu?.context
    if (!ctx || (ctx.type !== "document" && ctx.type !== "folder")) return false
    const raw =
      ctx.documentId || ctx.filePath || ctx.path || ""
    return raw ? findPinnedSidebarShortcut(shortcuts, raw) !== undefined : false
  }, [contextMenu, shortcuts])

  useEffect(() => {
    setContextMenu(null)
  }, [activeTab])

  // Listen for "reveal-in-sidebar" — explicit user action: switch to browse and scroll to node
  useEffect(() => {
    const handleRevealInSidebar = (event: CustomEvent<{ filePath: string }>) => {
      const { filePath } = event.detail
      if (!filePath) return
      // Force switch to browse mode (even when already in a browse sub-view)
      setActiveTab('browse-nav')
      // Defer dispatch so BrowsePanel is mounted (matters when switching from graph/tags/trash)
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('show-document-in-browse', {
          detail: { filePath, documentId: filePath, forceRefresh: false, scrollToNode: true },
        }))
      }, 50)
    }
    window.addEventListener('reveal-in-sidebar', handleRevealInSidebar as EventListener)
    return () => window.removeEventListener('reveal-in-sidebar', handleRevealInSidebar as EventListener)
  }, [setActiveTab])

  // Listen for context menu opened from main panel folder view (same menu as tree)
  useEffect(() => {
    const handler = (event: CustomEvent<{ x: number; y: number; context: ContextMenuContext }>) => {
      const { x, y, context } = event.detail
      setContextMenu({ x, y, context })
    }
    window.addEventListener('open-context-menu', handler as EventListener)
    return () => window.removeEventListener('open-context-menu', handler as EventListener)
  }, [])

  const normalizePath = useCallback((value?: string) => {
    if (!value) return undefined
    return value.startsWith('content/') ? value.substring(8) : value
  }, [])

  const openContextMenu = useCallback((context: ContextMenuContext, event: React.MouseEvent) => {
    event.preventDefault()
    setContextMenu({
      x: event.clientX,
      y: event.clientY,
      context
    })
  }, [])

  const handleTreeContextMenu = useCallback((node: TreeData, event: React.MouseEvent) => {
    const type = node.type === 'file' ? 'document' : node.type === 'project' ? 'project' : 'folder'
    openContextMenu({
      type,
      value: node.name,
      path: node.path || node.id,
      filePath: node.type === 'file' ? (node.path || node.id) : undefined,
      documentId: node.type === 'file' ? node.id : undefined,
    }, event)
  }, [openContextMenu])

  const handleDocumentContextMenu = useCallback((document: DocumentListItemData, event: React.MouseEvent) => {
    openContextMenu({
      type: 'document',
      value: document.title,
      filePath: document.filePath || document.path,
      documentId: document.id,
      tags: document.tags,
    }, event)
  }, [openContextMenu])

  const handleFolderNodeContextMenu = useCallback((
    node: FolderTreeNode,
    event: React.MouseEvent,
    workspaceId?: string,
    browseHiddenChildCount?: number,
    browseHiddenLabel?: string
  ) => {
    openContextMenu({
      type: node.type === 'file' ? 'document' : 'folder',
      value: node.name,
      path: node.path,
      filePath: node.type === 'file' ? node.path : undefined,
      documentId: node.type === 'file' ? node.id : undefined,
      ...(workspaceId ? { workspaceId } : {}),
      ...(browseHiddenChildCount != null ? { browseHiddenChildCount } : {}),
      ...(browseHiddenLabel ? { browseHiddenLabel } : {}),
    }, event)
  }, [openContextMenu])

  const browseOpenTabIndex = useMemo(() => buildOpenTabIndex(openTabs), [openTabs])
  const browseOpenTabPaths = useMemo(
    () => openTabPathsFromIndex(browseOpenTabIndex),
    [browseOpenTabIndex],
  )

  const handleTagContextMenu = useCallback((tag: string, event: React.MouseEvent) => {
    openContextMenu({
      type: 'tag',
      value: tag,
    }, event)
  }, [openContextMenu])

  const closeContextMenu = useCallback(() => setContextMenu(null), [])

  const handleScopedSearch = useCallback((payload: {
    query: string
    context: ContextMenuContext
    path?: string
  }) => {
    setContextMenu(null)
    const fromContext =
      payload.context.type === 'folder' || payload.context.type === 'project'
        ? (payload.context.path || payload.context.value)
        : null
    const folderPath = (payload.path || fromContext || '').trim()
    window.dispatchEvent(
      new CustomEvent('flownotes:open-search-view', {
        detail: {
          query: '',
          ...(folderPath ? { path: folderPath } : {}),
        },
      }),
    )
  }, [])

  const handleCreateFile = useCallback(async (folderPath: string, workspaceId?: string) => {
    try {
      console.log('[CreateFile] Creating document in folder:', folderPath)

      const resolvedWorkspaceId =
        workspaceId ??
        (folderPath
          ? resolveWorkspaceForDocument(
              folderPath,
              allWorkspaces.map((w) => ({ id: w.id, contentRoot: w.contentRoot })),
            )
          : undefined)

      const { createDocument } = await import('../../lib/api')
      const result = await createDocument({
        title: 'Untitled Note',
        content: '',
        document_type: 'note',
        folder_path: folderPath,
        ...(resolvedWorkspaceId ? { workspace_id: resolvedWorkspaceId } : {}),
      })

      // Open document in new tab - prefer UUID so tab bar + URL stay aligned
      if (result.document) {
        const filePath = result.document.filePath
        const documentId = result.document.id
        markDocumentForEditOnOpen(documentId)
        if (filePath) {
          markDocumentForEditOnOpen(filePath)
        }
        console.log('[CreateFile] Opening document:', { filePath, documentId, folderPath })
        onDocumentClick(documentId)
      }

      // Refresh file tree to show new document
      window.dispatchEvent(new CustomEvent('document-created', {
        detail: {
          path: result.document?.filePath || result.document?.id,
          folderPath: folderPath
        }
      }))

      // Show success toast
      const { toast } = await import('sonner')
      // NEVER show opaque ws/{hex} storage roots in toasts - use formatUserFacingStoragePath
      toast.success('Document created', {
        description: `Created in ${formatUserFacingStoragePath(folderPath, allWorkspaces)}`,
      })
    } catch (error) {
      console.error('Failed to create file:', error)
      const { toast } = await import('sonner')
      toast.error('Failed to create file', {
        description: error instanceof Error ? error.message : 'Unknown error'
      })
    }
  }, [onDocumentClick, allWorkspaces])

  const handleImportTextFiles = useCallback(async (folderPath: string, workspaceId?: string) => {
    try {
      const { pickLocalTextImportFiles, importLocalTextFiles } = await import('../../lib/local-text-import')
      const files = await pickLocalTextImportFiles(true)
      if (files.length === 0) return

      const resolvedWorkspaceId =
        workspaceId ??
        (folderPath
          ? resolveWorkspaceForDocument(
              folderPath,
              allWorkspaces.map((w) => ({ id: w.id, contentRoot: w.contentRoot })),
            )
          : undefined)

      const { createDocument } = await import('../../lib/api')
      const { imported, errors } = await importLocalTextFiles({
        files,
        folderPath,
        workspaceId: resolvedWorkspaceId,
        createDocument,
      })

      for (const doc of imported) {
        window.dispatchEvent(new CustomEvent('document-created', {
          detail: { path: doc.filePath || doc.id, folderPath },
        }))
      }

      if (imported.length > 0) {
        const last = imported[imported.length - 1]
        onDocumentClick(last.id)
        const { toast } = await import('sonner')
        // NEVER show opaque ws/{hex} storage roots in toasts - use formatUserFacingStoragePath
        const folderLabel = formatUserFacingStoragePath(folderPath, allWorkspaces)
        toast.success(
          imported.length === 1 ? 'Imported document' : `Imported ${imported.length} documents`,
          { description: `Into ${folderLabel}` },
        )
      }

      if (errors.length > 0) {
        const { toast } = await import('sonner')
        toast.error(errors.length === 1 ? 'Import failed' : `${errors.length} files failed`, {
          description: errors.map((e) => `${e.name}: ${e.reason}`).slice(0, 3).join('; '),
        })
      }
    } catch (error) {
      console.error('Failed to import text files:', error)
      const { toast } = await import('sonner')
      toast.error('Failed to import', {
        description: error instanceof Error ? error.message : 'Unknown error',
      })
    }
  }, [onDocumentClick, allWorkspaces])

  const handleImportTextFilesDrop = useCallback(async (
    folderPath: string,
    fileList: FileList | File[],
    workspaceId?: string,
    options?: {
      tabPlacement?: 'left' | 'right' | 'replace'
      relativeToTabId?: string | null
    },
  ) => {
    const files = Array.from(fileList)
    if (files.length === 0) return

    try {
      const { partitionLocalTextImportFiles, importLocalTextFiles } = await import('../../lib/local-text-import')
      const { checkDroppedImageImport, hasImageDocExtension } = await import('../../lib/image-document')
      const imageCandidates = files.filter((file) => hasImageDocExtension(file.name))
      const nonImageFiles = files.filter((file) => !hasImageDocExtension(file.name))
      const { textFiles, otherFiles, rejected } = partitionLocalTextImportFiles(nonImageFiles)
      const imageOk: File[] = []
      const imageRejected: Array<{ name: string; reason: string }> = []
      for (const file of imageCandidates) {
        const check = checkDroppedImageImport(file)
        if (check.ok) imageOk.push(file)
        else imageRejected.push({ name: file.name, reason: check.reason })
      }

      if (otherFiles.length > 0 || rejected.length > 0 || imageRejected.length > 0) {
        const { toast } = await import('sonner')
        const reasons = [
          ...rejected.map((r) => r.reason),
          ...imageRejected.map((r) => r.reason),
          ...otherFiles.map((f) => `${f.name}: only notes (.md, .txt) and images are supported`),
        ]
        toast.error('Some files were skipped', {
          description: reasons.slice(0, 3).join('; '),
        })
      }

      if (textFiles.length === 0 && imageOk.length === 0) return

      const resolvedWorkspaceId =
        workspaceId ??
        (folderPath
          ? resolveWorkspaceForDocument(
              folderPath,
              allWorkspaces.map((w) => ({ id: w.id, contentRoot: w.contentRoot })),
            )
          : undefined)

      const imported: Array<{ id: string; filePath?: string }> = []
      const errors: Array<{ name: string; reason: string }> = []

      if (textFiles.length > 0) {
        const { createDocument } = await import('../../lib/api')
        const textResult = await importLocalTextFiles({
          files: textFiles,
          folderPath,
          workspaceId: resolvedWorkspaceId,
          createDocument,
        })
        for (const doc of textResult.imported) {
          imported.push({ id: doc.id, filePath: doc.filePath })
        }
        errors.push(...textResult.errors)
      }

      if (imageOk.length > 0) {
        const { uploadImageDocumentFile } = await import('../../lib/api/image-documents')
        for (const file of imageOk) {
          try {
            const uploaded = await uploadImageDocumentFile({ file, folderPath })
            imported.push({ id: uploaded.documentId, filePath: uploaded.filePath })
          } catch (error) {
            errors.push({
              name: file.name,
              reason: error instanceof Error ? error.message : 'Upload failed',
            })
          }
        }
      }

      for (const doc of imported) {
        window.dispatchEvent(new CustomEvent('document-created', {
          detail: { path: doc.filePath || doc.id, folderPath },
        }))
      }

      if (imported.length > 0) {
        const last = imported[imported.length - 1]
        // Tab-bar drops pass tabPlacement; Browse/folder drops omit it (keep default open intent).
        const placement = options?.tabPlacement
        const relativeToTabId = options?.relativeToTabId ?? null

        if (!placement || placement === 'replace') {
          onDocumentClick(last.id)
        } else {
          // Force a new tab, then slide it left/right of the drop anchor.
          const modEvent = {
            metaKey: true,
            ctrlKey: false,
            button: 0,
            preventDefault: () => {},
            stopPropagation: () => {},
          } as unknown as ReactMouseEvent
          onDocumentClick(last.id, modEvent)
          window.setTimeout(() => {
            window.dispatchEvent(
              new CustomEvent('place-open-tab-relative', {
                detail: {
                  documentId: last.id,
                  relativeToTabId,
                  placement,
                },
              }),
            )
          }, 80)
        }

        const { toast } = await import('sonner')
        // NEVER show opaque ws/{hex} storage roots in toasts - use formatUserFacingStoragePath
        const folderLabel = formatUserFacingStoragePath(folderPath, allWorkspaces)
        toast.success(
          imported.length === 1 ? 'Imported document' : `Imported ${imported.length} documents`,
          { description: `Into ${folderLabel}` },
        )
      }

      if (errors.length > 0) {
        const { toast } = await import('sonner')
        toast.error('Import failed', {
          description: errors.map((e) => `${e.name}: ${e.reason}`).slice(0, 3).join('; '),
        })
      }
    } catch (error) {
      console.error('Failed to import dropped text files:', error)
      const { toast } = await import('sonner')
      toast.error('Failed to import', {
        description: error instanceof Error ? error.message : 'Unknown error',
      })
    }
  }, [onDocumentClick, allWorkspaces])

  const handlePinToSidebar = useCallback(async (
    rawIdOrPath: string,
    currentlyPinned: boolean,
    targetType: 'document' | 'folder' = 'document',
  ) => {
    try {
      const refreshList = async () => {
        const next = await getSidebarShortcuts()
        setShortcuts(next.shortcuts || [])
      }

      if (currentlyPinned) {
        const shortcut = findPinnedSidebarShortcut(shortcuts, rawIdOrPath)
        if (!shortcut) {
          const { toast } = await import('sonner')
          toast.error('Could not find this pin', {
            description: 'Try refreshing the sidebar or pin again from the file tree.',
          })
          return
        }
        await deleteSidebarShortcut(shortcut.id)
        const { toast } = await import('sonner')
        toast.success(targetType === 'folder' ? 'Unpinned folder from sidebar' : 'Unpinned from sidebar')
      } else if (targetType === 'folder') {
        await createSidebarShortcut({ folder_path: rawIdOrPath })
        const { toast } = await import('sonner')
        toast.success('Pinned folder to sidebar')
      } else {
        const uuid = await resolveDocumentRowId(rawIdOrPath)
        await createSidebarShortcut({ document_id: uuid })
        const { toast } = await import('sonner')
        toast.success('Pinned to sidebar')
      }
      await refreshList()
      window.dispatchEvent(new CustomEvent('sidebar-shortcuts-changed'))
    } catch (error) {
      console.error('Failed to toggle pin:', error)
      const { toast } = await import('sonner')
      toast.error(currentlyPinned ? 'Failed to unpin' : 'Failed to pin', {
        description: error instanceof Error ? error.message : 'Unknown error'
      })
    }
  }, [shortcuts])

  useEffect(() => {
    const onPinToggle = (event: Event) => {
      const detail = (event as CustomEvent<{ idOrPath?: string; targetType?: 'document' | 'folder' }>).detail
      const idOrPath = detail?.idOrPath?.trim()
      if (!idOrPath) return
      const targetType = detail.targetType === 'folder' ? 'folder' : 'document'
      void handlePinToSidebar(idOrPath, findPinnedSidebarShortcut(shortcuts, idOrPath) != null, targetType)
    }
    window.addEventListener('sidebar-pin-toggle', onPinToggle)
    return () => window.removeEventListener('sidebar-pin-toggle', onPinToggle)
  }, [handlePinToSidebar, shortcuts])

  const handleCreateFolder = useCallback(async (parentPath: string) => {
    try {
      const folderName = prompt('Enter folder name:')
      if (!folderName || !folderName.trim()) {
        return
      }

      if (folderName.includes('/') || folderName.includes('\\')) {
        const { toast } = await import('sonner')
        toast.error('Invalid folder name', {
          description: 'Folder name cannot contain slashes'
        })
        return
      }

      const normalizedParent = String(parentPath || '')
        .replace(/\\/g, '/')
        .replace(/^\/+/, '')
        .replace(/\/+$/, '')
      const newFolderPath = normalizedParent
        ? `${normalizedParent}/${folderName.trim()}`
        : folderName.trim()

      const { createFolder } = await import('../../lib/api')
      await createFolder(newFolderPath)

      window.dispatchEvent(new CustomEvent('folder-created', {
        detail: { path: newFolderPath }
      }))

      const { toast } = await import('sonner')
      toast.success('Folder created', {
        description: folderName.trim(),
      })
    } catch (error) {
      console.error('Failed to create folder:', error)
      const { toast } = await import('sonner')
      toast.error('Failed to create folder', {
        description: error instanceof Error ? error.message : 'Unknown error'
      })
    }
  }, [])

  const handleOpenDocument = useCallback((documentId: string) => {
    onDocumentClick(documentId)
  }, [onDocumentClick])

  // Listen for empty-workspace "New" button (create file at path)
  useEffect(() => {
    const handleCreateFileAtPath = (event: CustomEvent<{ folderPath: string; workspaceId?: string }>) => {
      const folderPath = event.detail?.folderPath ?? ''
      const workspaceId = event.detail?.workspaceId
      handleCreateFile(folderPath, workspaceId)
    }
    const handleCreateFolderAtPath = (event: CustomEvent<{ parentPath: string }>) => {
      const parentPath = event.detail?.parentPath ?? ''
      handleCreateFolder(parentPath)
    }
    window.addEventListener('create-file-at-path', handleCreateFileAtPath as EventListener)
    window.addEventListener('create-folder-at-path', handleCreateFolderAtPath as EventListener)
    const handleImportTextAtPath = (event: CustomEvent<{ folderPath: string; workspaceId?: string }>) => {
      void handleImportTextFiles(event.detail?.folderPath ?? '', event.detail?.workspaceId)
    }
    const handleImportTextDropAtPath = (event: CustomEvent<{
      folderPath: string
      workspaceId?: string
      files: File[]
      tabPlacement?: 'left' | 'right' | 'replace'
      relativeToTabId?: string | null
    }>) => {
      void handleImportTextFilesDrop(
        event.detail?.folderPath ?? '',
        event.detail?.files ?? [],
        event.detail?.workspaceId,
        {
          tabPlacement: event.detail?.tabPlacement,
          relativeToTabId: event.detail?.relativeToTabId,
        },
      )
    }
    window.addEventListener('import-text-at-path', handleImportTextAtPath as EventListener)
    window.addEventListener('import-text-drop-at-path', handleImportTextDropAtPath as EventListener)
    return () => {
      window.removeEventListener('create-file-at-path', handleCreateFileAtPath as EventListener)
      window.removeEventListener('create-folder-at-path', handleCreateFolderAtPath as EventListener)
      window.removeEventListener('import-text-at-path', handleImportTextAtPath as EventListener)
      window.removeEventListener('import-text-drop-at-path', handleImportTextDropAtPath as EventListener)
    }
  }, [handleCreateFile, handleCreateFolder, handleImportTextFiles, handleImportTextFilesDrop])

  const [renameDialogOpen, setRenameDialogOpen] = useState(false)
  const [renameItem, setRenameItem] = useState<{
    type: "file" | "folder"
    id: string
    currentName: string
    path: string
    documentTitle?: string
    documentId?: string
    frontmatter?: Record<string, unknown> | null
  } | null>(null)

  const [moveDialogOpen, setMoveDialogOpen] = useState(false)
  const [moveItem, setMoveItem] = useState<{ id: string; path: string; type: 'file' | 'folder' } | null>(null)

  const handleRename = useCallback(async (item: { type: "file" | "folder"; id: string; currentName: string; path: string }) => {
    // For files, fetch document title + frontmatter for rename chips / AI suggest
    if (item.type === 'file') {
      try {
        let apiUrl: string
        if (item.id.includes('/')) {
          apiUrl = `/api/documents/by-path?path=${encodeURIComponent(item.id)}`
        } else {
          apiUrl = `/api/documents/${item.id}`
        }

        const response = await fetchWithAuth(apiUrl)
        if (response.ok) {
          const docData = await response.json()
          setRenameItem({
            ...item,
            documentTitle: docData.title,
            documentId: docData.id ?? (item.id.includes('/') ? undefined : item.id),
            frontmatter: docData.frontmatter ?? null,
          })
        } else {
          setRenameItem(item)
        }
      } catch (error) {
        console.error('Failed to fetch document title:', error)
        setRenameItem(item)
      }
    } else {
      setRenameItem(item)
    }
    setRenameDialogOpen(true)
  }, [])

  const handleRenameConfirm = useCallback(async (
    newName: string,
    options?: { title?: string; skipFilename?: boolean },
  ) => {
    if (!renameItem) return

    try {
      if (renameItem.type === 'file') {
        const { renameDocument, updateDocument } = await import('../../lib/api')
        const filename = newName.trim().endsWith('.md') ? newName.trim() : `${newName.trim()}.md`
        const oldBase = renameItem.currentName.replace(/\.md$/i, '')
        const newBase = filename.replace(/\.md$/i, '')
        const filenameChanged = !options?.skipFilename && oldBase !== newBase

        let renameResult: {
          success: boolean
          document: { id: string; oldPath: string; newPath: string }
        } | null = null

        if (filenameChanged) {
          renameResult = await renameDocument(renameItem.id, filename, {
            pathFallback: renameItem.path,
          })
        }

        if (options?.title?.trim()) {
          const titleId =
            renameResult?.document?.id ||
            renameItem.documentId ||
            renameItem.id
          await updateDocument(
            titleId,
            {
              title: options.title.trim(),
              frontmatter: {
                ...(renameItem.frontmatter || {}),
                title: options.title.trim(),
              },
              writeToFile: true,
            },
            {
              pathFallback: renameResult?.document?.newPath || renameItem.path,
            },
          )
        }

        const { toast } = await import('sonner')
        if (renameResult?.success && renameResult.document.newPath) {
          toast.success('Document renamed', {
            description: `${renameItem.currentName} → ${filename}`,
          })
          window.dispatchEvent(new CustomEvent('document-renamed', {
            detail: {
              oldPath: renameResult.document.oldPath,
              newPath: renameResult.document.newPath,
            },
          }))
        } else if (options?.title?.trim()) {
          toast.success('Document title updated')
          window.dispatchEvent(new CustomEvent('document-updated', {
            detail: {
              id: renameItem.documentId || renameItem.id,
              title: options.title.trim(),
            },
          }))
        }
      } else {
        // Folder rename
        const { renameFolder } = await import('../../lib/api')
        const result = await renameFolder(renameItem.path, newName.trim())

        if (result.success) {
          const { toast } = await import('sonner')
          toast.success('Folder renamed', {
            description: `${renameItem.currentName} → ${newName.trim()}${result.folder.documentsUpdated > 0 ? ` (${result.folder.documentsUpdated} files updated)` : ''}`
          })

          // Refresh file tree
          window.dispatchEvent(new CustomEvent('folder-renamed', {
            detail: { oldPath: result.folder.oldPath, newPath: result.folder.newPath }
          }))
        }
      }
    } catch (error) {
      console.error('Failed to rename:', error)
      throw error // Let dialog handle error display
    }
  }, [renameItem])

  const handleMove = useCallback((item: { type: "file" | "folder"; id: string; currentPath: string }) => {
    setMoveItem({ id: item.id, path: item.currentPath, type: item.type })
    setMoveDialogOpen(true)
  }, [])

  const handleMoveConfirm = useCallback(async (newFolderPath: string) => {
    if (!moveItem) return

    const { toast } = await import('sonner')

    try {
      if (moveItem.type === 'folder') {
        // List all documents under the folder
        const listRes = await fetchWithAuth(
          `/api/documents?pathPrefix=${encodeURIComponent(moveItem.path)}&limit=1000`
        )
        if (!listRes.ok) throw new Error('Failed to list folder contents')
        const listData = await listRes.json()
        const docs = listData.documents as { id: string; filePath: string }[]
        if (!docs?.length) {
          toast.info('Folder is empty', { description: 'No documents to move.' })
          setMoveDialogOpen(false)
          setMoveItem(null)
          return
        }

        const prefix = moveItem.path.replace(/\/+$/, '')
        const folderName = prefix.split('/').filter(Boolean).pop() || ''
        const destRoot = newFolderPath.replace(/\/+$/, '')
        const destPrefix = folderName ? `${destRoot}/${folderName}` : destRoot
        let moved = 0
        for (const doc of docs) {
          const relativePath = (doc.filePath || '').slice(prefix.length).replace(/^\//, '')
          const newPath = relativePath ? `${destPrefix}/${relativePath}` : destPrefix
          const res = await fetchWithAuth(`/api/documents/${doc.id}/move`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ newPath })
          })
          if (!res.ok) {
            const err = await res.json().catch(() => ({}))
            throw new Error(err.error || `Failed to move ${doc.filePath}`)
          }
          const result = await res.json()
          window.dispatchEvent(new CustomEvent('document-moved', {
            detail: { oldPath: result.document.oldPath, newPath: result.document.newPath }
          }))
          moved++
        }

        setMoveDialogOpen(false)
        setMoveItem(null)
        window.dispatchEvent(new CustomEvent('folder-moved', {
          detail: { oldPath: prefix, newPath: destPrefix, documentsMoved: moved },
        }))
        toast.success('Folder moved', { description: `${moved} document${moved === 1 ? '' : 's'} moved.` })
        return
      }

      // Single file move
      let documentId = moveItem.id
      if (moveItem.id.includes('/')) {
        const docResponse = await fetchWithAuth(`/api/documents/by-path?path=${encodeURIComponent(moveItem.id)}`)
        if (!docResponse.ok) throw new Error('Document not found')
        const docData = await docResponse.json()
        documentId = docData.id
      }

      const filename = moveItem.path.split('/').pop() || 'document.md'
      const newPath = newFolderPath ? `${newFolderPath}/${filename}` : filename

      const response = await fetchWithAuth(`/api/documents/${documentId}/move`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ newPath })
      })

      if (!response.ok) {
        let errorMessage = 'Failed to move document'
        try {
          const error = await response.json()
          errorMessage = error.error || errorMessage
        } catch {
          errorMessage = `Server error: ${response.status} ${response.statusText}`
        }
        throw new Error(errorMessage)
      }

      const result = await response.json()
      const capturedDocumentId = documentId
      const oldPath = result.document.oldPath
      const newPathResult = result.document.newPath

      setMoveDialogOpen(false)
      setMoveItem(null)

      toast.success('Document moved', {
        description: `Moved to ${newPath}`,
        duration: 10000,
        action: {
          label: 'Undo',
          onClick: async () => {
            try {
              const undoResponse = await fetchWithAuth(`/api/documents/${capturedDocumentId}/move`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ newPath: oldPath })
              })
              if (undoResponse.ok) {
                const undoResult = await undoResponse.json()
                toast.success('Move undone', { description: 'Document restored to original location' })
                window.dispatchEvent(new CustomEvent('document-moved', {
                  detail: { oldPath: undoResult.document.newPath, newPath: undoResult.document.oldPath }
                }))
                window.dispatchEvent(new CustomEvent('document-path-updated', {
                  detail: {
                    documentId: capturedDocumentId,
                    oldPath: undoResult.document.newPath,
                    newPath: undoResult.document.oldPath
                  }
                }))
                window.dispatchEvent(new CustomEvent('document-updated', {
                  detail: { documentId: undoResult.document.oldPath, filePath: undoResult.document.oldPath }
                }))
              }
            } catch (error) {
              toast.error('Failed to undo', {
                description: error instanceof Error ? error.message : 'Unknown error'
              })
            }
          }
        }
      })

      window.dispatchEvent(new CustomEvent('document-moved', {
        detail: { oldPath, newPath: newPathResult }
      }))
      window.dispatchEvent(new CustomEvent('document-path-updated', {
        detail: { documentId: capturedDocumentId, oldPath, newPath: newPathResult }
      }))
      window.dispatchEvent(new CustomEvent('document-updated', {
        detail: { documentId: newPathResult, filePath: newPathResult }
      }))
    } catch (error) {
      console.error('Move failed:', error)
      toast.error('Move failed', {
        description: error instanceof Error ? error.message : 'Unknown error'
      })
    }
  }, [moveItem])

  const handleDelete = useCallback(async (item: { type: "file" | "folder"; id: string; name: string; path: string }) => {
    const { toast } = await import('sonner')

    if (item.type === 'folder') {
      toast(`Delete folder "${item.name}"?`, {
        description: 'Removes the folder and any documents inside. This cannot be undone.',
        duration: 10000,
        action: {
          label: 'Delete',
          onClick: async () => {
            try {
              const { deleteFolder } = await import('../../lib/api')
              const result = await deleteFolder(item.path, { recursive: true })
              const docNote =
                result.documentsDeleted > 0
                  ? ` (${result.documentsDeleted} document${result.documentsDeleted === 1 ? '' : 's'} removed)`
                  : ''
              toast.success('Folder deleted', { description: `${item.name}${docNote}` })
              window.dispatchEvent(new CustomEvent('folder-deleted', {
                detail: { path: item.path, documentsDeleted: result.documentsDeleted },
              }))
              if (result.documentsDeleted > 0) {
                window.dispatchEvent(new CustomEvent('document-deleted', {
                  detail: { path: item.path, folderPath: item.path },
                }))
              }
            } catch (error) {
              console.error('Failed to delete folder:', error)
              toast.error('Failed to delete folder', {
                description: error instanceof Error ? error.message : 'Unknown error',
              })
            }
          },
        },
      })
      return
    }

    toast(`Delete document "${item.name}"?`, {
      description: 'This cannot be undone.',
      duration: 10000,
      action: {
        label: 'Delete',
        onClick: async () => {
          try {
            const { deleteDocument } = await import('../../lib/api')
            await deleteDocument(item.id)
            const { markDocumentRecentlyDeleted } = await import('../../lib/document-delete-utils')
            markDocumentRecentlyDeleted(item.id, item.path)
            toast.success('Document deleted', { description: item.name })
            window.dispatchEvent(new CustomEvent('document-deleted', {
              detail: { path: item.path, documentId: item.id, filePath: item.path }
            }))
          } catch (error) {
            console.error('Failed to delete:', error)
            toast.error('Failed to delete', {
              description: error instanceof Error ? error.message : 'Unknown error'
            })
          }
        }
      }
    })
  }, [])

  // Folder view "More options" menu (document-list-toolbar) reuses the same dialogs as the folder context menu.
  useEffect(() => {
    const folderFromEvent = (event: Event) => {
      const folderPath = String((event as CustomEvent<{ path?: string }>).detail?.path ?? '')
        .replace(/\\/g, '/')
        .replace(/^\/+|\/+$/g, '')
      if (!folderPath) return null
      return { folderPath, folderName: folderPath.split('/').pop() || folderPath }
    }
    const onRenameFolder = (event: Event) => {
      const f = folderFromEvent(event)
      if (f) void handleRename({ type: 'folder', id: f.folderPath, currentName: f.folderName, path: f.folderPath })
    }
    const onMoveFolder = (event: Event) => {
      const f = folderFromEvent(event)
      if (f) handleMove({ type: 'folder', id: f.folderPath, currentPath: f.folderPath })
    }
    const onDeleteFolder = (event: Event) => {
      const f = folderFromEvent(event)
      if (f) void handleDelete({ type: 'folder', id: f.folderPath, name: f.folderName, path: f.folderPath })
    }
    window.addEventListener('rename-folder-at-path', onRenameFolder)
    window.addEventListener('move-folder-at-path', onMoveFolder)
    window.addEventListener('delete-folder-at-path', onDeleteFolder)
    return () => {
      window.removeEventListener('rename-folder-at-path', onRenameFolder)
      window.removeEventListener('move-folder-at-path', onMoveFolder)
      window.removeEventListener('delete-folder-at-path', onDeleteFolder)
    }
  }, [handleRename, handleMove, handleDelete])

  const handleAddToChat = useCallback(async (item: { type: "file" | "folder"; id: string; path: string; title?: string }) => {
    if (!onAddToChat) return

    // Call the handler passed from parent
    // No toast notification - chip appearing in chat is visual feedback enough
    onAddToChat(item)
  }, [onAddToChat])

  // Expanded sidebar sits beside PanelResizeHandle (w-px). A border-r here
  // stacks a second pixel and makes the column edge thicker than the
  // panel header's border-b. Collapsed rail has no handle, so it keeps the edge.
  const columnEdgeClass = collapsed ? "border-r border-border" : ""

  // Don't render integration tabs while loading
  if (loading) {
    return (
      <div className={cn("h-full flex flex-col bg-sidebar panel-hover-glow", columnEdgeClass)}>
        <div className="p-4">
          <div className="text-sm text-muted-foreground">Loading integrations...</div>
        </div>
      </div>
    )
  }

  const activateMode = (fn: () => void) => {
    fn()
    if (!collapsed) return
    if (autoHide) openFlyout()
    else onToggleCollapse()
  }

  const primaryMode =
    activeTab === 'flows'
      ? 'flows'
      : activeTab === 'inbox'
        ? 'inbox'
        : activeTab === 'chats'
          ? 'chats'
          : activeTab === 'drive'
            ? 'drive'
            : activeTab === 'basecamp'
              ? 'basecamp'
              : activeTab === 'nextcloud'
                ? 'nextcloud'
                : activeTab === 'graph' || activeTab === 'tags' || activeTab === 'trash'
                ? null
                : 'browse'

  const suggestedRailId = suggestedExtensionRailId({
    primaryMode,
    activeTab:
      openTabs.find((t) => t.id === activeTabId || t.tabSlotId === activeTabId) ??
      (activeTabId ? { id: activeTabId } : null),
  })

  const coreRailItems: RailItem[] = [
    {
      id: 'browse',
      Icon: FolderTree,
      title: 'Browse',
      badge: null,
      onClick: () => activateMode(() => setActiveTab('browse-nav')),
    },
    {
      id: 'inbox',
      Icon: Inbox,
      title: 'Inbox',
      onClick: () =>
        activateMode(() => {
          setActiveTab('inbox')
          onInboxClick?.()
        }),
      badge: inboxUnreadCount > 0 ? (inboxUnreadCount > 99 ? '99+' : String(inboxUnreadCount)) : null,
    },
    {
      id: 'flows',
      Icon: Zap,
      title: 'Flows',
      badge: null,
      onClick: () => activateMode(() => setActiveTab('flows')),
    },
    {
      id: 'chats',
      Icon: Bot,
      title: 'Chats',
      badge: null,
      onClick: () => activateMode(() => setActiveTab('chats')),
    },
  ]

  const extensionRailItems: RailItem[] = [
    {
      id: 'drive',
      Icon: GoogleDriveIcon,
      title: 'Google Drive',
      badge: null,
      onClick: () =>
        activateMode(() => {
          setActiveTab('drive')
          onDriveClick?.()
        }),
    },
  ]
  if (showBasecampRail) {
    extensionRailItems.push({
      id: 'basecamp',
      Icon: BasecampIcon,
      title: 'Basecamp',
      badge: null,
      onClick: () => activateMode(() => setActiveTab('basecamp')),
      testId: testIds.leftSidebar.railBasecamp,
    })
  }
  if (showNextcloudRail) {
    extensionRailItems.push({
      id: 'nextcloud',
      Icon: NextcloudIcon,
      title: 'Nextcloud',
      badge: null,
      onClick: () => activateMode(() => setActiveTab('nextcloud')),
      testId: testIds.leftSidebar.railNextcloud,
    })
  }

  const iconRail = (
    <LeftSidebarIconRail
      collapsed={collapsed}
      onToggleCollapse={onToggleCollapse}
      coreRailItems={coreRailItems}
      extensionRailItems={extensionRailItems}
      primaryMode={primaryMode}
      suggestedRailId={suggestedRailId}
      driveConnected={Boolean(driveAuthStatus?.connected)}
      basecampDisconnected={basecampDisconnected}
      onAddExtension={() =>
        openWorkspaceExtensionsSettings(
          activeWorkspaceIds[0] ?? allWorkspaces[0]?.id,
        )
      }
      onOpenSettings={() => openSettings()}
    />
  )

  // Mode panel: title first. Open Tabs drawer sits at the bottom.
  const modeColumn = (
        <div className="flex min-h-0 min-w-0 max-w-full flex-1 flex-col overflow-hidden" data-testid={testIds.leftSidebar.panel}>

          {/* Panel content. min-h-0 so the Open Tabs height resolves against a definite block. */}
          <div
            ref={sidebarPanelContentRef}
            className="flex min-h-0 flex-1 flex-col overflow-hidden min-w-0 max-w-full overflow-x-hidden"
          >
            <SidebarPanelHeader
              data-testid={testIds.leftSidebar.panelHeader}
              backSlot={
                (activeTab === "documents-tree" || activeTab === "documents") ? (
                  <button
                    onClick={() => setActiveTab('browse-nav')}
                    className="flex items-center gap-1 text-xs text-sidebar-foreground/50 hover:text-sidebar-foreground transition-colors flex-none px-1.5 py-0.5 rounded hover:bg-sidebar-accent/60"
                    title="Back to Browse"
                  >
                    <ChevronRight className="h-3 w-3 rotate-180 flex-none" />
                    <span>Browse</span>
                  </button>
                ) : undefined
              }
              title={
                activeTab === "documents-tree" ? "Documents"
                  : activeTab === "documents" ? "Documents"
                  : activeTab === "graph" ? "Graph"
                  : activeTab === "flows" ? "Flows"
                  : activeTab === "chats" ? "Chats"
                  : activeTab === "drive" ? "Google Drive"
                  : activeTab === "basecamp" ? (
                    <span className="inline-flex min-w-0 items-center gap-1.5">
                      <span className="truncate">Basecamp</span>
                      {basecampDisconnected ? (
                        <span title="Basecamp is disconnected" className="inline-flex shrink-0">
                          <AlertTriangle
                            className="h-3.5 w-3.5 text-amber-400"
                            aria-label="Basecamp is disconnected"
                            data-testid={testIds.basecampSidebar.headerAlert}
                          />
                        </span>
                      ) : null}
                    </span>
                  )
                  : activeTab === "nextcloud" ? "Nextcloud"
                  : activeTab === "inbox" ? "Inbox"
                  : activeTab === "tags" ? "Tags"
                  : activeTab === "trash" ? "Trash"
                  : "Browse"
              }
              optionsMenu={
                activeTab === "chats" ? (
                  <ChatsSidebarPanelMenu
                    dense={sidebarDense}
                    onDenseChange={setSidebarDense}
                    openTabsVisible={sidebarOpenTabsVisible}
                    onOpenTabsVisibleChange={setSidebarOpenTabsVisible}
                    collapsed={collapsed}
                    onCollapseSidebar={onToggleCollapse}
                  />
                ) : activeTab === "drive" ? (
                  <DriveOptionsMenu
                    connected={Boolean(driveAuthStatus?.connected)}
                    onConnect={handleDriveConnect}
                    onImportFromDrive={handleDriveImportFromDrive}
                    openTabsVisible={sidebarOpenTabsVisible}
                    onOpenTabsVisibleChange={setSidebarOpenTabsVisible}
                    collapsed={collapsed}
                    onCollapseSidebar={onToggleCollapse}
                  />
                ) : activeTab === "nextcloud" ? (
                  <NextcloudOptionsMenu
                    organizationId={nextcloudFocusId || drivePanelOrgId}
                    openTabsVisible={sidebarOpenTabsVisible}
                    onOpenTabsVisibleChange={setSidebarOpenTabsVisible}
                    collapsed={collapsed}
                    onCollapseSidebar={onToggleCollapse}
                  />
                ) : (
                  <SidebarPanelMenu
                    openTabsVisible={sidebarOpenTabsVisible}
                    onOpenTabsVisibleChange={setSidebarOpenTabsVisible}
                    collapsed={collapsed}
                    onCollapseSidebar={onToggleCollapse}
                    onOpenTrash={() => setActiveTab('trash')}
                    dense={sidebarDense}
                    onDenseChange={setSidebarDense}
                    basecamp={
                      activeTab === "basecamp"
                        ? {
                            connected: !basecampDisconnected && Boolean(basecampAuthStatus?.authenticated),
                            authUrl: basecampReconnect?.status?.auth_url ?? basecampAuthStatus?.auth_url,
                            organizationId: basecampReconnect?.id ?? drivePanelOrgId,
                          }
                        : undefined
                    }
                  />
                )
              }
            />

            <BrowseFolderFocusProvider
              workspaceIds={activeWorkspaceIds}
              activeDocumentPath={activeDocumentPath ?? null}
              openTabPaths={browseOpenTabPaths}
            >
            <div
              className="flex min-h-0 flex-1 flex-col overflow-hidden"
              data-sidebar-density={sidebarDense ? 'dense' : 'detailed'}
            >
            {/* ── Browse mode ──────────────────────────────────────────── */}
            <>
              {/* BrowsePanel nav — hidden only for full-takeover tools (graph, flows, tags, trash, drive) */}
              {activeTab !== "graph" &&
               activeTab !== "flows" &&
               activeTab !== "inbox" &&
               activeTab !== "chats" &&
               activeTab !== "drive" &&
               activeTab !== "basecamp" &&
               activeTab !== "nextcloud" &&
               activeTab !== "tags" &&
               activeTab !== "trash" && (
                <BrowsePanel
                activeTab={activeTab}
                activeTabId={activeTabId}
                onSetTab={setActiveTab}
                onHomeClick={onHomeClick}
                onInboxClick={onInboxClick}

                workspaces={allWorkspaces}
                activeWorkspaceIds={activeWorkspaceIds}
                configuredIntegrations={configuredIntegrations}
                openTabs={openTabs}
                onDocumentClick={onDocumentClick}
                onDocumentClose={onDocumentClose}
                onCloseAllOpenDocuments={onCloseAllOpenDocuments}
                activeDocumentPath={activeDocumentPath}
                selectedProjectId={selectedProjectId}
                onFolderClick={onProjectClick}
                onFolderTreeContextMenu={handleFolderNodeContextMenu}
                explorerRefreshToken={browseExplorerRefreshToken}
                onToggleWorkspace={toggleWorkspace}
                onHideOtherWorkspaces={hideOtherWorkspaces}
                shortcuts={shortcuts}
                shortcutsLoading={shortcutsLoading}
                activeShortcutId={activeShortcutId}
                sensors={sensors}
                onShortcutDragStart={handleShortcutDragStart}
                onShortcutDragEnd={handleShortcutDragEnd}
                />
              )}

              {/* Sidebar-resident full-takeover views */}
              {activeTab === "graph" && <GraphView />}
              {activeTab === "tags" && <TagsView onTagContextMenu={handleTagContextMenu} />}
              {activeTab === "flows" && (
                <FlowsManager
                  onOpenDocument={onDocumentClick}
                  activeDocumentPath={activeDocumentPath}
                  activeDocumentId={activeTabId}
                  dense={sidebarDense}
                />
              )}
              {activeTab === "inbox" && <InboxSidebarView dense={sidebarDense} />}
              {activeTab === "chats" && (
                <ChatsSidebarPanel dense={sidebarDense} activeEditorTabId={activeTabId} />
              )}
              {activeTab === "trash" && (
                <TrashView
                  workspaceIds={activeWorkspaceIds}
                  onDocumentClick={onDocumentClick}
                  onRestore={(documentId) => {
                    window.dispatchEvent(new CustomEvent('document-restored', { detail: { documentId } }))
                  }}
                />
              )}
              {activeTab === "drive" && (
                <DriveSidebar
                  dense={sidebarDense}
                  organizationId={drivePanelOrgId}
                  organizationIds={activeWorkspaceIds}
                  onOpenDrivePreview={onOpenDrivePreview}
                  onOpenFolderView={onDriveClick}
                  onOpenLinkedDocument={onDocumentClick}
                  openTabs={openTabs}
                  activeTabId={activeTabId}
                />
              )}
              <DriveImportPickerHost
                fallbackOrganizationId={drivePanelOrgId}
                onOpenDocument={onDocumentClick}
                openTabs={openTabs}
                activeTabId={activeTabId}
                selectedProjectId={selectedProjectId}
              />
              {activeTab === "basecamp" && showBasecampRail && (
                <BasecampSidebarPanel
                  dense={sidebarDense}
                  organizationIds={activeWorkspaceIds}
                  activeTabId={activeTabId}
                  openTabs={openTabs}
                />
              )}
              {activeTab === "nextcloud" && showNextcloudRail && (
                <NextcloudSidebarPanel
                  organizationId={nextcloudFocusId || undefined}
                  organizationIds={nextcloudCandidates}
                  onOrganizationIdChange={setPickedNextcloudId}
                />
              )}
            </>
            </div>
            {contextMenu && (
              <ContextMenu
                x={contextMenu.x}
                y={contextMenu.y}
                context={contextMenu.context}
                onClose={closeContextMenu}
                onSearchIn={handleScopedSearch}
                onCreateFile={handleCreateFile}
                onCreateFolder={handleCreateFolder}
                onImportTextFiles={handleImportTextFiles}
                onOpen={handleOpenDocument}
                onPreview={(item) => {
                  window.dispatchEvent(new CustomEvent('explorer-preview-item', { detail: item }))
                  closeContextMenu()
                }}
                onRename={handleRename}
                onMove={handleMove}
                onDelete={handleDelete}
                onAddToChat={handleAddToChat}
                onPinToSidebar={handlePinToSidebar}
                isPinned={sidebarShortcutContextIsPinned}
              />
            )}
            </BrowseFolderFocusProvider>

            {/* Open Tabs drawer. Do not gate on primaryMode. */}
            {sidebarOpenTabsVisible && (
              <SidebarOpenTabsSection
                splitLayoutContainerRef={sidebarPanelContentRef}
                hasOpenTabs={hasOpenTabs}
                tabsByWorkspace={tabsByWorkspace}
                openTabsTotalCount={openTabsTotalCount}
                activeTabId={activeTabId}
                activeWorkspaceIds={activeWorkspaceIds}
                onDocumentClick={onDocumentClick}
                onDocumentClose={onDocumentClose}
                onCloseManyDocuments={onCloseManyDocuments}
                onToggleWorkspace={toggleWorkspace}
                onHideOtherWorkspaces={hideOtherWorkspaces}
                tabMenuHandlers={tabMenuHandlers}
              />
            )}

          </div>

        </div>

  )

  const sidebarDialogs = (
    <>
    {renameItem && (
      <RenameDialog
        open={renameDialogOpen}
        onOpenChange={(open) => {
          setRenameDialogOpen(open)
          if (!open) setRenameItem(null)
        }}
        type={renameItem.type}
        currentName={renameItem.currentName}
        documentTitle={renameItem.documentTitle}
        documentId={renameItem.documentId}
        frontmatter={renameItem.frontmatter}
        pathFallback={renameItem.path}
        onRename={handleRenameConfirm}
      />
    )}

    {moveItem && (
      <FolderPickerDialog
        open={moveDialogOpen}
        onOpenChange={(open) => {
          setMoveDialogOpen(open)
          if (!open) setMoveItem(null)
        }}
        currentPath={moveItem.path}
        mode={moveItem.type}
        workspaces={moveDialogWorkspaces}
        onSelect={handleMoveConfirm}
      />
    )}
    </>
  )

  return (
    <div className={cn("h-full flex flex-col bg-sidebar min-w-0 max-w-full overflow-hidden panel-hover-glow group/sidebar", columnEdgeClass)} data-testid={testIds.leftSidebar.container}>
      <div className="flex h-full min-w-0 max-w-full overflow-hidden">
        {iconRail}
        {collapsed ? null : (
          <div ref={setDockedModeSlot} className="flex h-full min-h-0 min-w-0 flex-1 flex-col" />
        )}
      </div>
      <StablePortal slot={collapsed ? flyoutPortal : dockedModeSlot}>
        {modeColumn}
        {sidebarDialogs}
      </StablePortal>
    </div>
  )
}
